import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Receipt, ArrowRight, XCircle } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { useOrganization } from "@/hooks/useOrganization";
import { downloadReceipt } from "../BookingReceipt";
import { BookingRefundCancelDialog } from "@/components/admin/schedule/BookingRefundCancelDialog";
import type { Tables } from "@/integrations/supabase/types";
import type { BookingDetail, PlayerDisplayInfo } from "./types";

interface BookingDetailActionsProps {
  booking: BookingDetail;
  courtName: string;
  allPlayers: PlayerDisplayInfo[];
  payments: Tables<"booking_payments">[];
  totalAmount: number;
  paidAmount: number;
  bookerEmail: string | null;
  onMoveBooking?: () => void;
  onCancelled?: () => void;
}

export function BookingDetailActions({
  booking,
  courtName,
  allPlayers,
  payments,
  totalAmount,
  paidAmount,
  bookerEmail,
  onMoveBooking,
  onCancelled,
}: BookingDetailActionsProps) {
  const { t } = useTranslation("admin");
  const { isAdmin } = useUserRole();
  const { organization } = useOrganization();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  return (
    <>
      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => {
            const mainPayment = payments.find((p) => ["paid", "manual"].includes(p.status)) ?? payments[0];
            downloadReceipt({
              bookingId: booking.id,
              orgName: organization?.name ?? "Klubben",
              orgAddress: organization?.address,
              orgLogoUrl: organization?.logo_url,
              courtName,
              startTime: booking.start_time,
              endTime: booking.end_time,
              durationMinutes: booking.duration_minutes,
              pinCode: booking.pin_code,
              players: allPlayers.map((p) => p.name),
              totalAmount,
              paidAmount,
              paymentStatus: mainPayment?.status ?? "unpaid",
              paymentMethod: mainPayment?.payment_method ?? "pending",
              createdAt: booking.created_at,
            });
          }}
        >
          <Receipt className="h-3.5 w-3.5" />
          Ladda ner kvitto
        </Button>
      </div>

      {/* Admin: Move booking */}
      {isAdmin && onMoveBooking && booking.status === "confirmed" && (
        <div className="pt-2">
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={onMoveBooking}>
            <ArrowRight className="h-3.5 w-3.5" />
            Flytta bokning
          </Button>
        </div>
      )}

      {/* Admin: Cancel booking */}
      {isAdmin && booking.status === "confirmed" && (
        <div className="pt-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setShowCancelDialog(true)}
          >
            <XCircle className="h-3.5 w-3.5" />
            {t("adminCancel.cancelBooking")}
          </Button>
        </div>
      )}

      {/* Lager 2.1 — ersätter befintliga AdminCancelBookingDialog.
          Decision 10.3: en delad dialog för alla avbokningar.
          kind='private' eftersom legacy open-match-bokningar har samma
          payment-shape (aggregerad). Canonical open-match (Lager 2.3)
          öppnar via ActivityDetailModal separat. */}
      <BookingRefundCancelDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        kind="private"
        initialMode={null /* user väljer själv via radio (regression-coverage) */}
        booking={booking}
        payments={payments}
        bookerEmail={bookerEmail}
        playerCount={allPlayers.length}
        onCancelled={() => {
          setShowCancelDialog(false);
          onCancelled?.();
        }}
      />
    </>
  );
}
